import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CreateReferenceData, ErrorResponse, MainResponse, RejectedValue, TranslationList } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import {
    Applicant, ApplicantCreateData, ApplicantAttachment, ApplicantResponse, ApplicantToReassign, Attachments, Education, Experience,
    FeedbackRequest, FeedbackResponse, HiringRequest, HiringRequestCreateData, initialApplicantsInSliceValue, initialHiringRequestInSliceValue,
    initialSliceValue, NewApplicantRequest, NewApplicantResponse, NewApplicantToAssign, AttachmentsResponse, Skills, GeneralFeedbackRequest,
    PersonalFeedbackRequest, ApplicantStatusChange, DeleteApplicantRequest, ApplicationHistoryItem, ApplicantPhaseChange,
    HiringRequestByPosition, ApplicantForEmployeeResponse, ApplicantForEmployeeRequest, ApplicationHistoryRequest, GetEmailsRequest, EmailsResponse,
    UpdateEmailRequest, DownloadAttachmentRequest
} from "./applicant-tracking-interfaces";
import { RootState } from "app/store";
import { downloadFile } from "shared/utils/common";
import { SourceTypes } from "constants/enums";
import { setEmployeeFromApplicant } from "../../TenantSettings/Employee/state/employeeSlice";
import { setContactFromApplicant } from "../../BusinessConnections/Contacts/state/contactSlice";
import { API_BASE_URL as baseUri } from "config/env";

//#region HiringRequest

export const GetAllHiringRequests = createAsyncThunk<HiringRequest[], void, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetAllHiringRequests',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/hiring-requests/get-all-active', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as HiringRequest[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllInactiveHiringRequests = createAsyncThunk<HiringRequest[], void, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetAllInactiveHiringRequests',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/hiring-requests/get-all-inactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as HiringRequest[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetHiringRequest = createAsyncThunk<HiringRequest, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetHiringRequest',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/hiring-requests/get?hiringRequestId=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as HiringRequest;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetHiringRequestCreateData = createAsyncThunk<HiringRequestCreateData, void, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetHiringRequestCreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/hiring-requests/get-create-data', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as HiringRequestCreateData;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateHiringRequest = createAsyncThunk<MainResponse, HiringRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/CreateHiringRequest',
    async (request: HiringRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/hiring-requests/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetApplicantCreateData())
            dispatch(GetAllHiringRequests())
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateHiringRequestByPosition = createAsyncThunk<MainResponse, HiringRequestByPosition, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/CreateHiringRequestByPosition',
    async (request: HiringRequestByPosition, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/hiring-requests/create-other', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetApplicantCreateData())
            dispatch(GetAllHiringRequests())
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateHiringRequest = createAsyncThunk<MainResponse, HiringRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/UpdateHiringRequest',
    async (request: HiringRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/hiring-requests/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetApplicantCreateData())
            dispatch(GetAllHiringRequests())
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeactivateHiringRequest = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/DeactivateHiringRequest',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/hiring-requests/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(GetApplicantCreateData())
            dispatch(GetAllHiringRequests());
            dispatch(GetAllInactiveHiringRequests());
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ReactivateHiringRequest = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/ReactivateHiringRequest',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/hiring-requests/reactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(GetApplicantCreateData())
            dispatch(GetAllHiringRequests());
            dispatch(GetAllInactiveHiringRequests());
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region NewApplicant

export const GetEmails = createAsyncThunk<EmailsResponse, GetEmailsRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetEmails',
    async (request: GetEmailsRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/new-applicants/get-applicant-emails', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as EmailsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const LoadMoreEmails = createAsyncThunk<EmailsResponse, GetEmailsRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/LoadMoreEmails',
    async (request: GetEmailsRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/new-applicants/get-applicant-emails', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as EmailsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const MarkEmail = createAsyncThunk<MainResponse, UpdateEmailRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/MarkEmail',
    async (request: UpdateEmailRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/new-applicants/mark-email', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetNewApplicants = createAsyncThunk<NewApplicantResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetNewApplicants',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/new-applicants/get-all-active', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as NewApplicantResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetNewApplicant = createAsyncThunk<NewApplicantResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetNewApplicant',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/new-applicants/get?newApplicantId=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as NewApplicantResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateNewApplicant = createAsyncThunk<MainResponse, NewApplicantRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/CreateNewApplicant',
    async (request: NewApplicantRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/new-applicants/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetNewApplicants())
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateNewApplicant = createAsyncThunk<MainResponse, NewApplicantRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/UpdateNewApplicant',
    async (request: NewApplicantRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/new-applicants/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetNewApplicants());
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const AssignNewApplicantToHiringRequest = createAsyncThunk<MainResponse, NewApplicantToAssign, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/AssignNewApplicantToHiringRequest',
    async (request: NewApplicantToAssign, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/assign', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetNewApplicants());
            dispatch(GetAssignedApplicants());
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteNewApplicant = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/DeleteNewApplicant',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/new-applicants/delete?id=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            dispatch(GetNewApplicants());
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateApplicantSource = createAsyncThunk<TranslationList[], CreateReferenceData, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/CreateApplicantSource',
    async (data: CreateReferenceData, { rejectWithValue, getState }) => {
        const response = await fetch(baseUri + 'api/source/create-from-dropdown', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({ name: data.name, type: SourceTypes.Applicant })
        });
        if (response.status === 200) {
            const createResponse = await response.json() as TranslationList[];
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#endregion

//#region Applicant

export const GetAssignedApplicants = createAsyncThunk<ApplicantResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetAssignedApplicants',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/applicants/get-assigned-applicants', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ApplicantResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetApplicant = createAsyncThunk<ApplicantResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetApplicant',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/get?applicantId=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(resetApplicant())
            const responseModel = await response.json() as ApplicantResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetApplicantForEmployee = createAsyncThunk<ApplicantForEmployeeResponse, ApplicantForEmployeeRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetApplicantForEmployee',
    async (request: ApplicantForEmployeeRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/get?applicantId=' + request.applicantId + '&hiringRequestId=' + request.hiringRequestId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(resetApplicant())
            const responseModel = await response.json() as ApplicantForEmployeeResponse;
            dispatch(setEmployeeFromApplicant(responseModel));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetApplicantForContact = createAsyncThunk<ApplicantResponse, ApplicantForEmployeeRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetApplicantForContact',
    async (request: ApplicantForEmployeeRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/get?applicantId=' + request.applicantId + '&hiringRequestId=' + request.hiringRequestId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(resetApplicant())
            const responseModel = await response.json() as ApplicantResponse;
            dispatch(setContactFromApplicant(responseModel));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetApplicantCreateData = createAsyncThunk<ApplicantCreateData, void, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetApplicantCreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/applicants/get-create-data', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ApplicantCreateData;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateApplicant = createAsyncThunk<MainResponse, Applicant, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/UpdateApplicant',
    async (request: Applicant, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetAssignedApplicants())
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ReassignApplicantToHiringRequest = createAsyncThunk<MainResponse, ApplicantToReassign, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/ReassignApplicantToHiringRequest',
    async (request: ApplicantToReassign, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/reassign', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetAssignedApplicants());
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ChangeApplicantStatus = createAsyncThunk<MainResponse, ApplicantStatusChange, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/ChangeApplicantStatus',
    async (request: ApplicantStatusChange, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/update-status', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetAssignedApplicants());
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ChangeApplicantPhase = createAsyncThunk<MainResponse, ApplicantPhaseChange, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/ChangeApplicantPhase',
    async (request: ApplicantPhaseChange, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/update-phase', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetAssignedApplicants());
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteApplicantPhase = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/DeleteApplicantPhase',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/delete-phase?id=' + id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(deletePhase(id))
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteApplicant = createAsyncThunk<MainResponse, DeleteApplicantRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/DeleteApplicant',
    async (request: DeleteApplicantRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/delete', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetAssignedApplicants());
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region ApplicantEducation

export const GetApplicantEducation = createAsyncThunk<Education, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetApplicantEducation',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/applicant-education/get?applicantId=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Education;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateApplicantEducation = createAsyncThunk<MainResponse, Education, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/CreateApplicantEducation',
    async (request: Education, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicant-education/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetApplicantEducation(request.applicantId!))
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateApplicantEducation = createAsyncThunk<MainResponse, Education, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/UpdateApplicantEducation',
    async (request: Education, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicant-education/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetApplicantEducation(request.applicantId!))
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region ApplicantExperience

export const GetApplicantExperience = createAsyncThunk<Experience, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetApplicantExperience',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/applicant-experience/get?applicantId=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Experience;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateApplicantExperience = createAsyncThunk<MainResponse, Experience, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/CreateApplicantExperience',
    async (request: Experience, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicant-experience/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetApplicantExperience(request.applicantId!))
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateApplicantExperience = createAsyncThunk<MainResponse, Experience, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/UpdateApplicantExperience',
    async (request: Experience, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicant-experience/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetApplicantExperience(request.applicantId!))
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region ApplicantSkills

export const GetApplicantSkills = createAsyncThunk<Skills, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetApplicantSkills',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/applicant-skills/get?applicantId=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Skills;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateApplicantSkills = createAsyncThunk<MainResponse, Skills, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/CreateApplicantSkills',
    async (request: Skills, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/applicant-skills/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateApplicantSkills = createAsyncThunk<MainResponse, Skills, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/UpdateApplicantSkills',
    async (request: Skills, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicant-skills/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetApplicantSkills(request.applicantId!))
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region ApplicantFeedback

export const GetApplicantFeedback = createAsyncThunk<Record<string, FeedbackResponse | null>, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetApplicantFeedback',
    async (id: string, { rejectWithValue, getState }) => {
        const response = await fetch(baseUri + 'api/applicant-feedback/get?applicantId=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Record<string, FeedbackResponse | null>;
            const reduxStore = getState() as RootState;
            const employeeId = reduxStore.authentificationSlice.userSettings?.employeeId;

            for (const key in responseModel) {
                const feedback = responseModel[key];
                if (!feedback) continue;
                const foundComment = feedback.reviewerComments.find(comment => comment.employeeId === employeeId);

                feedback.feedbackComment = foundComment?.feedbackComment || null;
                feedback.grade = foundComment?.grade || null;
            }

            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateUpdateApplicantFeedback = createAsyncThunk<MainResponse, FeedbackRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/CreateUpdateApplicantFeedback',
    async (request: FeedbackRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicant-feedback/create-update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetApplicantFeedback(request.applicantId!));
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateApplicantGeneralFeedback = createAsyncThunk<MainResponse, GeneralFeedbackRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/UpdateApplicantGeneralFeedback',
    async (request: GeneralFeedbackRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicant-feedback/update-general-feedback', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetApplicantFeedback(request.applicantId!));
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateApplicantPersonalFeedback = createAsyncThunk<MainResponse, PersonalFeedbackRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/UpdateApplicantPersonalFeedback',
    async (request: PersonalFeedbackRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicant-feedback/update-employee-feedback', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetApplicantFeedback(request.applicantId!));
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region ApplicantFiles

export const GetApplicantFiles = createAsyncThunk<ApplicantAttachment[], string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetApplicantFiles',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/applicants/get-files?applicantId=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ApplicantAttachment[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UploadApplicantFiles = createAsyncThunk<AttachmentsResponse, Attachments, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/UploadApplicantFile',
    async (request: Attachments, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/applicants/upload-files', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AttachmentsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteApplicantFile = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/DeleteApplicantFile',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/applicants/delete-file?id=' + id, {
            method: "DELETE",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DownloadApplicantFile = createAsyncThunk<ApplicantAttachment, DownloadAttachmentRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/DownloadApplicantFile',
    async (request: DownloadAttachmentRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/applicants/download-file', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ApplicantAttachment;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

//#region ApplicationHistory

export const GetApplicationHistory = createAsyncThunk<ApplicationHistoryItem[], string, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/GetApplicationHistory',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/applicants/get-history?applicantId=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ApplicationHistoryItem[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateApplicationHistory = createAsyncThunk<MainResponse, ApplicationHistoryRequest, { rejectValue: RejectedValue }>(
    'Slice/ApplicantTrackingSlice/UpdateApplicationHistory',
    async (request: ApplicationHistoryRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/applicants/update-history', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            dispatch(GetAssignedApplicants())
            if (request.applicantId) {
                dispatch(GetApplicationHistory(request.applicantId))
            }
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

export const ApplicantTrackingSlice = createSlice({
    name: "ApplicantTrackingSlice",
    initialState: initialSliceValue,
    reducers: {
        resetApplicantTrackingSlice: (state) => {
            state.statusCode = '';
            state.message = '';
            state.isLoading = false;
            state.hiringRequests = initialHiringRequestInSliceValue;
            state.applicants = initialApplicantsInSliceValue;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
        },
        resetHiringRequest: (state) => {
            state.statusCode = '';
            state.message = '';
            state.isLoading = false;
            state.hiringRequests.hiringRequest = null;
        },
        resetNewApplicant: (state) => {
            state.statusCode = '';
            state.message = '';
            state.isLoading = false;
            state.applicants.newApplicant = null;
        },
        resetApplicant: (state) => {
            state.statusCode = '';
            state.message = '';
            state.isLoading = false;
            state.applicants.applicant = null;
            state.applicants.education = null;
            state.applicants.experience = null;
            state.applicants.feedbacks = null;
            state.applicants.attachments = null;
            state.applicants.skills = null;
            state.applicants.applicationHistory = null;
        },
        deletePhase: (state, action: PayloadAction<string>) => {
            state.applicants.applicationHistory?.forEach(historyItem => {
                historyItem.phaseIds = historyItem.phaseIds.filter(
                    phase => phase.hiringRequestApplicantPhaseId !== action.payload
                );
            });
        },
        resetEmails: (state) => {
            state.emailData = {
                messages: [],
                nextPageLink: '',
                isLoading: false,
                isLoadingMore: false,
                tenantId: ""
            }
        },
        setTenantId: (state, action: PayloadAction<string | undefined | null>) => {
            state.emailData.tenantId = action.payload ?? "";
        }
    },
    extraReducers(builder) {
        //#region HiringRequest
        //Get all hiring requests
        builder
            .addCase(GetAllHiringRequests.fulfilled, (state, action: PayloadAction<HiringRequest[]>) => {
                // state.isLoading = false;
                state.message = '';
                state.hiringRequests.hiringRequests = action.payload.filter(hr => !hr.isDefault);
                const grouped = action.payload.reduce<Record<string, HiringRequest[]>>((acc, request) => {
                    if (!acc[request.positionId]) {
                        acc[request.positionId] = [];
                    }
                    acc[request.positionId].push(request);
                    return acc;
                }, {});

                for (const key in grouped) {
                    grouped[key].sort((a, b) => {
                        if (a.isDefault) return 1;
                        if (b.isDefault) return -1;
                        return 0;
                    });
                }

                state.hiringRequests.hiringRequestByPositionList = grouped;
            })
            .addCase(GetAllHiringRequests.rejected, (state, action) => {
                // state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllHiringRequests.pending, state => {
                // state.isLoading = true;
            })
        //Get all inactive hiring requests
        builder
            .addCase(GetAllInactiveHiringRequests.fulfilled, (state, action: PayloadAction<HiringRequest[]>) => {
                // state.isLoading = false;
                state.message = '';
                state.hiringRequests.inactiveHiringRequests = action.payload.filter(hr => !hr.isDefault);
            })
            .addCase(GetAllInactiveHiringRequests.rejected, (state, action) => {
                // state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAllInactiveHiringRequests.pending, state => {
                // state.isLoading = true;
            })
        //Get hiring request
        builder
            .addCase(GetHiringRequest.fulfilled, (state, action: PayloadAction<HiringRequest>) => {
                state.hiringRequests.isModalLoading = false;
                state.message = '';
                state.hiringRequests.hiringRequest = action.payload;
            })
            .addCase(GetHiringRequest.rejected, (state, action) => {
                state.hiringRequests.isModalLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetHiringRequest.pending, state => {
                state.hiringRequests.isModalLoading = true;
            })
        //Get hiring request create data
        builder
            .addCase(GetHiringRequestCreateData.fulfilled, (state, action: PayloadAction<HiringRequestCreateData>) => {
                state.hiringRequests.isModalLoading = false;
                state.message = '';
                state.hiringRequests.createData = action.payload;
            })
            .addCase(GetHiringRequestCreateData.rejected, (state, action) => {
                state.hiringRequests.isModalLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetHiringRequestCreateData.pending, state => {
                state.hiringRequests.isModalLoading = true;
            })
        //Create hiring request
        builder
            .addCase(CreateHiringRequest.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The hiring request is successfully created.';
                state.statusCode = "200";
                state.hiringRequests.isModalLoading = false;
                state.hiringRequests.hiringRequest = { ...action.meta.arg, id: action.payload.id }
            })
            .addCase(CreateHiringRequest.rejected, (state, action) => {
                state.hiringRequests.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateHiringRequest.pending, state => {
                state.hiringRequests.isModalLoading = true;
            })
        //Create hiring request
        builder
            .addCase(CreateHiringRequestByPosition.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The hiring request is successfully created.';
                state.statusCode = "200";
                state.hiringRequests.isModalLoading = false;
            })
            .addCase(CreateHiringRequestByPosition.rejected, (state, action) => {
                state.hiringRequests.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateHiringRequestByPosition.pending, state => {
                state.hiringRequests.isModalLoading = true;
            })
        //Update hiring request
        builder
            .addCase(UpdateHiringRequest.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The hiring request is successfully updated.';
                state.statusCode = "200";
                state.hiringRequests.isModalLoading = false;
                state.hiringRequests.hiringRequest = { ...action.meta.arg, id: action.payload.id }
            })
            .addCase(UpdateHiringRequest.rejected, (state, action) => {
                state.hiringRequests.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateHiringRequest.pending, state => {
                state.hiringRequests.isModalLoading = true;
            })
        //Deactivate hiring request
        builder
            .addCase(DeactivateHiringRequest.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The hiring request is successfully deactivated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
            })
            .addCase(DeactivateHiringRequest.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeactivateHiringRequest.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Deactivate hiring request
        builder
            .addCase(ReactivateHiringRequest.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The hiring request is successfully reactivated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
            })
            .addCase(ReactivateHiringRequest.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateHiringRequest.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //#endregion

        //#region NewApplicant
        //Get emails
        builder
            .addCase(GetEmails.fulfilled, (state, action: PayloadAction<EmailsResponse>) => {
                state.message = '';
                state.emailData = { ...action.payload, isLoading: false, isLoadingMore: false, tenantId: state.emailData.tenantId };
            })
            .addCase(GetEmails.rejected, (state, action) => {
                state.emailData.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetEmails.pending, state => {
                state.emailData.isLoading = true;
            })
        //Get emails
        builder
            .addCase(LoadMoreEmails.fulfilled, (state, action: PayloadAction<EmailsResponse>) => {
                state.message = '';
                action.payload.messages.forEach(msg => {
                    if (!state.emailData.messages.some(existing => existing.message.id === msg.message.id)) {
                        state.emailData.messages.push(msg);
                    }
                });
                state.emailData.nextPageLink = action.payload.nextPageLink;
                state.emailData.isLoadingMore = false;
            })
            .addCase(LoadMoreEmails.rejected, (state, action) => {
                state.emailData.isLoadingMore = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(LoadMoreEmails.pending, state => {
                state.emailData.isLoadingMore = true;
            })
        //Mark email
        builder
            .addCase(MarkEmail.fulfilled, (state, action) => {
                state.message = action.payload.message;
                state.statusCode = "200";
                state.emailData.messages = state.emailData.messages.map(msg =>
                    msg.message.id === action.payload.id
                        ? { ...msg, isCreated: action.meta.arg.isCreated }
                        : msg
                );
            })
            .addCase(MarkEmail.rejected, (state, action) => {
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(MarkEmail.pending, state => {
            })
        //Get new applicants
        builder
            .addCase(GetNewApplicants.fulfilled, (state, action: PayloadAction<NewApplicantResponse[]>) => {
                // state.isLoading = false;
                state.message = '';
                state.applicants.newApplicantList = action.payload;
            })
            .addCase(GetNewApplicants.rejected, (state, action) => {
                // state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetNewApplicants.pending, state => {
                // state.isLoading = true;
            })
        //Get new applicant
        builder
            .addCase(GetNewApplicant.fulfilled, (state, action: PayloadAction<NewApplicantResponse>) => {
                state.applicants.isModalLoading = false;
                state.message = '';
                state.applicants.newApplicant = action.payload;
            })
            .addCase(GetNewApplicant.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetNewApplicant.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Create new applicant
        builder
            .addCase(CreateNewApplicant.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant is successfully created.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                let newApplicants = state.applicants.newApplicantList;
                newApplicants.push({ ...action.meta.arg, id: action.payload.id, date: new Date() });
                state.applicants.newApplicantList = newApplicants;
            })
            .addCase(CreateNewApplicant.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateNewApplicant.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Update applicant
        builder
            .addCase(UpdateNewApplicant.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant is successfully updated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
            })
            .addCase(UpdateNewApplicant.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateNewApplicant.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Update applicant
        builder
            .addCase(AssignNewApplicantToHiringRequest.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant is successfully assigned.';
                state.statusCode = "200";
                // state.isLoading = false;
            })
            .addCase(AssignNewApplicantToHiringRequest.rejected, (state, action) => {
                // state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(AssignNewApplicantToHiringRequest.pending, state => {
                // state.isLoading = true;
            })
        //Delete new applicant
        builder
            .addCase(DeleteNewApplicant.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant is successfully deleted.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
            })
            .addCase(DeleteNewApplicant.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteNewApplicant.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //CreateSource
        builder
            .addCase(CreateApplicantSource.fulfilled, (state, action: PayloadAction<TranslationList[]>) => {
                state.message = 'The source is successfully created on application default language.';
                state.statusCode = "200";
                state.applicants.sourceInfo = {
                    isSourceDropdownLoading: false,
                    newSourceId: ""
                }
                if (state.applicants.createData) {
                    state.applicants.createData.applicantSources = action.payload;
                }
            })
            .addCase(CreateApplicantSource.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.applicants.sourceInfo.isSourceDropdownLoading = false;
            })
            .addCase(CreateApplicantSource.pending, state => {
                state.applicants.sourceInfo.isSourceDropdownLoading = true;
            })

        //#endregion

        //#region AssignedApplicant
        //Get assigned applicants
        builder
            .addCase(GetAssignedApplicants.fulfilled, (state, action: PayloadAction<ApplicantResponse[]>) => {
                // state.isLoading = false;
                state.message = '';

                // const applicantMap = action.payload.reduce((acc, applicant) => {
                //     if (!applicant.id) return acc;

                //     const existing = acc[applicant.id];

                //     if (
                //         !existing ||
                //         new Date(applicant.applicationDate) > new Date(existing.applicationDate)
                //     ) {
                //         acc[applicant.id] = applicant;
                //     }

                //     return acc;
                // }, {} as { [id: string]: ApplicantResponse });

                // const uniqueApplicants = Object.values(applicantMap);

                state.applicants.applicants = action.payload;
                state.applicants.applicantByHiringRequestList = action.payload.reduce<Record<string, ApplicantResponse[]>>((acc, applicant) => {
                    if (!acc[applicant.hiringRequestId]) {
                        acc[applicant.hiringRequestId] = [];
                    }
                    acc[applicant.hiringRequestId].push(applicant);
                    return acc;
                }, {});
            })
            .addCase(GetAssignedApplicants.rejected, (state, action) => {
                // state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetAssignedApplicants.pending, state => {
                // state.isLoading = true;
            })
        //Get applicant
        builder
            .addCase(GetApplicant.fulfilled, (state, action: PayloadAction<ApplicantResponse>) => {
                state.applicants.isModalLoading = false;
                state.message = '';
                state.applicants.applicant = action.payload;
            })
            .addCase(GetApplicant.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetApplicant.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Get applicant create data
        builder
            .addCase(GetApplicantCreateData.fulfilled, (state, action: PayloadAction<ApplicantCreateData>) => {
                state.applicants.isModalLoading = false;
                state.message = '';
                state.applicants.createData = action.payload;
            })
            .addCase(GetApplicantCreateData.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetApplicantCreateData.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Update applicant
        builder
            .addCase(UpdateApplicant.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant is successfully updated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                state.applicants.applicant = { ...action.meta.arg, id: action.payload.id }
            })
            .addCase(UpdateApplicant.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateApplicant.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Reassign applicant applicant
        builder
            .addCase(ReassignApplicantToHiringRequest.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant is successfully updated.';
                state.statusCode = "200";
                // state.isLoading = false;
            })
            .addCase(ReassignApplicantToHiringRequest.rejected, (state, action) => {
                // state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReassignApplicantToHiringRequest.pending, state => {
                // state.isLoading = true;
            })
        //Change applicant status
        builder
            .addCase(ChangeApplicantStatus.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant status is successfully updated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
            })
            .addCase(ChangeApplicantStatus.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ChangeApplicantStatus.pending, state => {
                state.applicants.isModalLoading = true;
            })

        //Change applicant phase
        builder
            .addCase(ChangeApplicantPhase.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant phase is successfully updated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
            })
            .addCase(ChangeApplicantPhase.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ChangeApplicantPhase.pending, state => {
                state.applicants.isModalLoading = true;
            })

        //Change applicant phase
        builder
            .addCase(DeleteApplicantPhase.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant phase is successfully deleted.';
                state.statusCode = "200";
                // state.applicants.isModalLoading = false;
            })
            .addCase(DeleteApplicantPhase.rejected, (state, action) => {
                // state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteApplicantPhase.pending, state => {
                // state.applicants.isModalLoading = true;
            })

        //Delete applicant
        builder
            .addCase(DeleteApplicant.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant is successfully deleted.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
            })
            .addCase(DeleteApplicant.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteApplicant.pending, state => {
                state.applicants.isModalLoading = true;
            })

        //#endregion

        //#region ApplicantEducation

        //Get Applicant Education
        builder
            .addCase(GetApplicantEducation.fulfilled, (state, action: PayloadAction<Education>) => {
                state.applicants.isModalLoading = false;
                state.message = '';
                state.applicants.education = action.payload;
            })
            .addCase(GetApplicantEducation.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetApplicantEducation.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Create applicant education
        builder
            .addCase(CreateApplicantEducation.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant education is successfully created.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                // state.applicants.education = action.meta.arg;
            })
            .addCase(CreateApplicantEducation.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateApplicantEducation.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Update applicant education
        builder
            .addCase(UpdateApplicantEducation.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant education is successfully updated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                // state.applicants.education = action.meta.arg;
            })
            .addCase(UpdateApplicantEducation.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateApplicantEducation.pending, state => {
                state.applicants.isModalLoading = true;
            })

        //#endregion 

        //#region ApplicantExperience

        //Get Applicant experience
        builder
            .addCase(GetApplicantExperience.fulfilled, (state, action: PayloadAction<Experience>) => {
                state.applicants.isModalLoading = false;
                state.message = '';
                state.applicants.experience = action.payload;
            })
            .addCase(GetApplicantExperience.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetApplicantExperience.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Create applicant experience
        builder
            .addCase(CreateApplicantExperience.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant experience is successfully created.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                // state.applicants.experience = action.meta.arg;
            })
            .addCase(CreateApplicantExperience.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateApplicantExperience.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Update applicant experience
        builder
            .addCase(UpdateApplicantExperience.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant experience is successfully updated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                // state.applicants.experience = action.meta.arg;
            })
            .addCase(UpdateApplicantExperience.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateApplicantExperience.pending, state => {
                state.applicants.isModalLoading = true;
            })

        //#endregion 

        //#region ApplicantSkills

        //Get Applicant skills
        builder
            .addCase(GetApplicantSkills.fulfilled, (state, action: PayloadAction<Skills>) => {
                state.applicants.isModalLoading = false;
                state.message = '';
                state.applicants.skills = action.payload;
            })
            .addCase(GetApplicantSkills.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetApplicantSkills.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Create applicant skills
        builder
            .addCase(CreateApplicantSkills.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant skills are successfully created.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                state.applicants.skills = action.meta.arg;
            })
            .addCase(CreateApplicantSkills.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateApplicantSkills.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Update applicant skills
        builder
            .addCase(UpdateApplicantSkills.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant skills are successfully updated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                // state.applicants.skills = action.meta.arg;
            })
            .addCase(UpdateApplicantSkills.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateApplicantSkills.pending, state => {
                state.applicants.isModalLoading = true;
            })

        //#endregion 

        //#region ApplicantFeedback

        //Get Applicant feedback
        builder
            .addCase(GetApplicantFeedback.fulfilled, (state, action: PayloadAction<Record<string, FeedbackResponse | null>>) => {
                state.applicants.isModalLoading = false;
                state.message = '';
                state.applicants.feedbacks = action.payload;
            })
            .addCase(GetApplicantFeedback.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetApplicantFeedback.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Create\Update applicant feedback
        builder
            .addCase(CreateUpdateApplicantFeedback.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant feedback is successfully created.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                // state.applicants.feedback = { ...action.meta.arg, reviewerComments: [] };
            })
            .addCase(CreateUpdateApplicantFeedback.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateUpdateApplicantFeedback.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Update applicant general feedback
        builder
            .addCase(UpdateApplicantGeneralFeedback.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant feedback is successfully updated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                // state.applicants.feedback = { ...action.meta.arg, reviewerComments: state.applicants.feedback?.reviewerComments ?? [] };
            })
            .addCase(UpdateApplicantGeneralFeedback.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateApplicantGeneralFeedback.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Update applicant personal feedback
        builder
            .addCase(UpdateApplicantPersonalFeedback.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant feedback is successfully updated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                // state.applicants.feedback = { ...action.meta.arg, reviewerComments: state.applicants.feedback?.reviewerComments ?? [] };
            })
            .addCase(UpdateApplicantPersonalFeedback.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateApplicantPersonalFeedback.pending, state => {
                state.applicants.isModalLoading = true;
            })

        //#endregion 

        //#region ApplicantFiles

        //Get Applicant files
        builder
            .addCase(GetApplicantFiles.fulfilled, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = '';
                state.applicants.attachments = { applicantId: action.meta.arg, files: action.payload };
            })
            .addCase(GetApplicantFiles.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetApplicantFiles.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Upload applicant files
        builder
            .addCase(UploadApplicantFiles.fulfilled, (state, action) => {
                state.message = 'The applicant file is successfully uploaded.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;

                if (!state.applicants.attachments) {
                    state.applicants.attachments = {
                        applicantId: action.meta.arg.applicantId,
                        files: action.payload.files
                    };
                } else {
                    state.applicants.attachments.files = action.payload.files;
                }
            })

            .addCase(UploadApplicantFiles.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UploadApplicantFiles.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Delete applicant file
        builder
            .addCase(DeleteApplicantFile.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant file is successfully deleted.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                if (state.applicants.attachments?.files) {
                    state.applicants.attachments.files = state.applicants.attachments.files.filter(
                        file => file.id !== action.payload.id
                    );
                }
            })
            .addCase(DeleteApplicantFile.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteApplicantFile.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Download applicant file
        builder
            .addCase(DownloadApplicantFile.fulfilled, (state, action) => {
                state.applicants.isModalLoading = false;
                downloadFile(action.payload.fileName, action.payload.fileBase64)
            })
            .addCase(DownloadApplicantFile.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DownloadApplicantFile.pending, state => {
                state.applicants.isModalLoading = true;
            })

        //#endregion

        //#region ApplicantSkills

        //Get Applicant skills
        builder
            .addCase(GetApplicationHistory.fulfilled, (state, action: PayloadAction<ApplicationHistoryItem[]>) => {
                state.applicants.isModalLoading = false;
                state.message = '';
                state.applicants.applicationHistory = action.payload;
            })
            .addCase(GetApplicationHistory.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetApplicationHistory.pending, state => {
                state.applicants.isModalLoading = true;
            })
        //Update applicant skills
        builder
            .addCase(UpdateApplicationHistory.fulfilled, (state, action) => {
                state.message = action.payload.message ?? 'The applicant history are successfully updated.';
                state.statusCode = "200";
                state.applicants.isModalLoading = false;
                // state.applicants.applicationHistory = action.meta.arg.applicantHistoryDTOs;
            })
            .addCase(UpdateApplicationHistory.rejected, (state, action) => {
                state.applicants.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateApplicationHistory.pending, state => {
                state.applicants.isModalLoading = true;
            })

        //#endregion 

    }
})

export const { resetApplicantTrackingSlice, resetStatusCode, resetHiringRequest, resetApplicant, resetNewApplicant, deletePhase, resetEmails, setTenantId } = ApplicantTrackingSlice.actions;
export default ApplicantTrackingSlice.reducer;